import { getRuntimeConfig, getRuntimeConfigOrDefault } from '../utils/runtime-config';

interface UploadConfig {
  mode: 'local' | 'cloud';
  localDir?: string;
  cloudBucket?: string;
  cloudRegion?: string;
}

interface UploadResult {
  success: boolean;
  url?: string;
  path?: string;
  error?: string;
}

class UploadService {
  private config: UploadConfig;

  constructor() {
    this.config = {
      mode: (getRuntimeConfig('REACT_APP_UPLOAD_MODE') as 'local' | 'cloud') || 'local',
      localDir: getRuntimeConfigOrDefault('REACT_APP_LOCAL_UPLOAD_DIR', 'uploads/resumes'),
      cloudBucket: getRuntimeConfig('REACT_APP_CLOUD_BUCKET_NAME'),
      cloudRegion: getRuntimeConfig('REACT_APP_CLOUD_REGION')
    };
  }

  async uploadResume(file: File, userId: string): Promise<UploadResult> {
    try {
      // Validate file
      const validationResult = this.validateFile(file);
      if (!validationResult.isValid) {
        return {
          success: false,
          error: validationResult.error
        };
      }

      // Generate unique filename
      const timestamp = Date.now();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const uniqueFileName = `${userId}_${timestamp}_${sanitizedFileName}`;

      if (this.config.mode === 'local') {
        return await this.uploadLocal(file, uniqueFileName);
      } else {
        return await this.uploadToCloud(file, uniqueFileName);
      }
    } catch (error) {
      console.error('Upload error:', error);
      return {
        success: false,
        error: 'Failed to upload file. Please try again.'
      };
    }
  }

  private validateFile(file: File): { isValid: boolean; error?: string } {
    // Check file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      return {
        isValid: false,
        error: 'File size must be less than 5MB'
      };
    }

    // Check file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: 'Only PDF, DOC, and DOCX files are allowed'
      };
    }

    return { isValid: true };
  }

  private async uploadLocal(file: File, fileName: string): Promise<UploadResult> {
    try {
      // Create FormData
      const formData = new FormData();
      formData.append('resume', file);
      formData.append('fileName', fileName);
      formData.append('uploadDir', this.config.localDir || 'uploads/resumes');

      // Send to backend API
      const response = await fetch(`${process.env.REACT_APP_API_URL}/upload/resume`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Upload failed');
      }

      const result = await response.json();
      return {
        success: true,
        path: result.path,
        url: result.url
      };
    } catch (error) {
      console.error('Local upload error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to upload file locally'
      };
    }
  }

  private async uploadToCloud(file: File, fileName: string): Promise<UploadResult> {
    try {
      // Get presigned URL from backend
      const presignedResponse = await fetch(`${process.env.REACT_APP_API_URL}/upload/presigned-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        },
        body: JSON.stringify({
          fileName,
          fileType: file.type,
          bucket: this.config.cloudBucket
        })
      });

      if (!presignedResponse.ok) {
        throw new Error('Failed to get upload URL');
      }

      const { uploadUrl, fileUrl } = await presignedResponse.json();

      // Upload directly to cloud storage
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type
        }
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload to cloud storage');
      }

      // Notify backend of successful upload
      await fetch(`${process.env.REACT_APP_API_URL}/upload/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        },
        body: JSON.stringify({
          fileName,
          fileUrl
        })
      });

      return {
        success: true,
        url: fileUrl,
        path: fileName
      };
    } catch (error) {
      console.error('Cloud upload error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to upload file to cloud'
      };
    }
  }

  async deleteResume(filePath: string): Promise<boolean> {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/upload/delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        },
        body: JSON.stringify({
          filePath,
          mode: this.config.mode
        })
      });

      return response.ok;
    } catch (error) {
      console.error('Delete error:', error);
      return false;
    }
  }
}

export default new UploadService();